/**
 * ============================================================
 * MODERN BLOGGER TEMPLATE - main.js (v3.0 — High-Tech Edition)
 * Project  : blogger-cdn
 * Brand    : Rihlah News
 * Version  : 3.0.0
 *
 * Perubahan v3.0:
 *   - Unified scroll handler (4→1 RAF loop, scroll lebih halus)
 *   - Card tilt via CSS class (bukan inline style per-event)
 *   - initNetCanvas() — animated particle network di landing
 *   - initCursorGlow() — cursor spotlight glow
 *   - initCommandPalette() — command palette Ctrl+K
 *   - initTOC() — auto table of contents artikel
 *   - initCodeCopy() — copy button pada code block
 *   - initLightbox() — image lightbox
 *   - initLiveClock() — jam live di landing page
 *   - initSkipLink() — skip to content (aksesibilitas)
 * ============================================================
 */

"use strict";

/* ============================================================
   HELPERS
   ============================================================ */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => ctx.querySelectorAll(sel);
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ============================================================
   1. HAMBURGER MENU
   ============================================================ */
function initHamburger() {
  const btn = $("#hamburger-btn");
  const menu = $("#mobile-nav");
  if (!btn || !menu) return;

  const closeMenu = () => {
    btn.classList.remove("is-active");
    menu.classList.remove("is-open");
    btn.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
  };

  on(btn, "click", () => {
    const isOpen = btn.classList.toggle("is-active");
    menu.classList.toggle("is-open", isOpen);
    btn.setAttribute("aria-expanded", String(isOpen));
    document.body.style.overflow = isOpen ? "hidden" : "";
  });

  document.addEventListener("click", (e) => {
    if (!btn.contains(e.target) && !menu.contains(e.target)) closeMenu();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeMenu();
  });
  $$("a", menu).forEach((a) => on(a, "click", closeMenu));
}

/* ============================================================
   2. UNIFIED SCROLL HANDLER
   Satu event listener + satu RAF untuk semua scroll work.
   Menggantikan: initStickyHeader, initBackToTop,
                 initArticleProgress, initScrollProgress.
   ============================================================ */
function initUnifiedScroll() {
  const header     = $(".site-header");
  const bttBtn     = $("#back-to-top");
  const progressEl = $("#scroll-progress");
  const article    = $(".post-content");
  const isPost     = !!$(".post-single");

  // Inject SVG ring ke back-to-top
  if (bttBtn && !bttBtn.querySelector("svg")) {
    bttBtn.insertAdjacentHTML(
      "beforeend",
      `<svg viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="27" cy="27" r="24" stroke="rgba(255,255,255,0.2)" stroke-width="2.5"/>
        <circle id="btt-ring" cx="27" cy="27" r="24"
          stroke="rgba(255,255,255,0.85)" stroke-width="2.5"
          stroke-linecap="round" stroke-dasharray="150.8" stroke-dashoffset="150.8"/>
      </svg>`,
    );
  }
  const ring = $("#btt-ring");

  on(bttBtn, "click", () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "instant" : "smooth" });
  });

  let ticking = false;

  const run = () => {
    const scrollY  = window.scrollY;
    const docH     = document.documentElement.scrollHeight - window.innerHeight;
    const globalPct = docH > 0 ? Math.min(scrollY / docH, 1) : 0;

    // Sticky header
    if (header) header.classList.toggle("scrolled", scrollY > 20);

    // Back-to-top + progress ring
    if (bttBtn) {
      bttBtn.classList.toggle("is-visible", scrollY > 400);
      if (ring) ring.style.strokeDashoffset = 150.8 - globalPct * 150.8;
    }

    // Scroll progress bar
    if (progressEl) {
      if (isPost && article) {
        // Pada artikel: progress mengukur posisi dalam konten artikel
        const headerH    = header?.offsetHeight ?? 64;
        const articleTop = article.getBoundingClientRect().top + scrollY - headerH;
        const pct        = Math.min(Math.max((scrollY - articleTop) / article.offsetHeight, 0), 1);
        progressEl.style.width = pct * 100 + "%";
        document.documentElement.style.setProperty("--reading-progress", pct * 100 + "%");
      } else {
        progressEl.style.width = globalPct * 100 + "%";
      }
    }

    ticking = false;
  };

  window.addEventListener("scroll", () => {
    if (!ticking) { window.requestAnimationFrame(run); ticking = true; }
  }, { passive: true });

  run(); // initial
}

/* ============================================================
   3. SMOOTH SCROLL
   ============================================================ */
function initSmoothScroll() {
  $$('a[href^="#"]').forEach((a) => {
    on(a, "click", (e) => {
      const id = a.getAttribute("href");
      if (id === "#") return;
      const target = $(id);
      if (!target) return;
      e.preventDefault();
      const headerH = $(".site-header")?.offsetHeight ?? 0;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - headerH - 16,
        behavior: prefersReducedMotion() ? "instant" : "smooth",
      });
    });
  });
}

/* ============================================================
   4. SCROLL ANIMATIONS (IntersectionObserver)
   ============================================================ */
function initScrollAnimations() {
  const els = $$(".fade-in, .slide-up, .slide-left, .slide-right, .scale-in");
  if (!els.length) return;
  if (prefersReducedMotion()) { els.forEach((el) => el.classList.add("is-visible")); return; }
  if (!("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("is-visible")); return; }

  const obs = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("is-visible"); obs.unobserve(e.target); }
    }),
    { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
  );
  els.forEach((el) => obs.observe(el));
}

/* ============================================================
   5. EXTERNAL LINKS
   ============================================================ */
function initExternalLinks() {
  const host = window.location.hostname;
  $$("a").forEach((a) => {
    const href = a.getAttribute("href");
    if (!href) return;
    try {
      const u = new URL(href, window.location.origin);
      if (u.hostname && u.hostname !== host) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
    } catch { /* relative link */ }
  });
}

/* ============================================================
   6. ACTIVE LINK HIGHLIGHTING
   ============================================================ */
function initActiveLinks() {
  const path   = window.location.pathname;
  const search = window.location.search;
  const label  = new URLSearchParams(search).get("label");

  $$(".site-nav a, .mobile-nav a").forEach((a) => {
    const href = a.getAttribute("href") || "";
    if (href === path || href === path + search) a.classList.add("active");
    if (href.includes("/search") && label) a.classList.add("active");
    if ((href === "/" || href === "") && path === "/") a.classList.add("active");
  });
}

/* ============================================================
   7. DARK MODE TOGGLE
   ============================================================ */
function initDarkMode() {
  const toggle = $("#dark-mode-toggle");
  const mobileToggle = $("#mobile-dark-mode-toggle");
  const body   = document.body;
  const KEY    = "blogger-theme";

  const apply = (isDark) => {
    body.classList.toggle("dark-mode", isDark);
    [toggle, mobileToggle].forEach((btn) => {
      if (!btn) return;
      const icon = btn.querySelector("i");
      if (icon) icon.className = isDark ? "fas fa-sun" : "fas fa-moon";
      btn.setAttribute("aria-label", isDark ? "Switch to light mode" : "Switch to dark mode");
    });
  };

  const saved  = localStorage.getItem(KEY);
  const sysDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  apply(saved === "dark" || (!saved && sysDark));

  const toggleTheme = () => {
    const isDark = !body.classList.contains("dark-mode");
    apply(isDark);
    localStorage.setItem(KEY, isDark ? "dark" : "light");
  };

  on(toggle, "click", toggleTheme);
  on(mobileToggle, "click", toggleTheme);

  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (e) => {
    if (!localStorage.getItem(KEY)) apply(e.matches);
  });
}

/* ============================================================
   8. COPY LINK / SHARE
   ============================================================ */
function getShareUrl() {
  const canonical = $('link[rel="canonical"]')?.href;
  return canonical || window.location.href;
}

function getShareTitle() {
  const title =
    $(".post-hero__title")?.textContent ||
    $(".post-title")?.textContent ||
    document.title;

  return title.replace(/\s+/g, " ").trim();
}

function getShareText() {
  const title = getShareTitle();
  const url = getShareUrl();
  return title ? `${title} ${url}` : url;
}

function setShareHref(selector, href) {
  $$(selector).forEach((link) => {
    link.href = href;
    link.setAttribute("rel", "noopener noreferrer");
    link.setAttribute("target", "_blank");
  });
}

function initArticleShare() {
  const url = getShareUrl();
  const title = getShareTitle();
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(getShareText());

  setShareHref(
    ".share-btn--twitter, .sidebar-share-btn--x",
    `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
  );
  setShareHref(
    ".share-btn--facebook, .sidebar-share-btn--fb",
    `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
  );
  setShareHref(
    ".share-btn--whatsapp, .sidebar-share-btn--wa",
    `https://api.whatsapp.com/send?text=${encodedText}`,
  );

  $$("[data-copy-link]").forEach((btn) => {
    btn.dataset.copyLink = url;
  });
}

async function writeClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back to execCommand below.
    }
  }

  const inp = document.createElement("textarea");
  inp.value = text;
  inp.setAttribute("readonly", "");
  inp.style.position = "fixed";
  inp.style.top = "-9999px";
  inp.style.opacity = "0";
  document.body.appendChild(inp);
  inp.focus();
  inp.select();
  inp.setSelectionRange(0, inp.value.length);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }
  document.body.removeChild(inp);
  return copied;
}

function initCopyLink() {
  $$("[data-copy-link]").forEach((btn) => {
    on(btn, "click", async () => {
      const url = btn.dataset.copyLink || getShareUrl();
      const copied = await writeClipboard(url);

      if (copied) {
        showToast("Link berhasil disalin!", "success");
        showCopyFeedback(btn, "Disalin!");
      } else {
        showToast("Link belum bisa disalin otomatis.", "error");
      }
    });
  });
}

function showCopyFeedback(btn, msg) {
  const orig = btn.innerHTML;
  btn.innerHTML = `<i class="fas fa-check"></i> ${msg}`;
  btn.disabled = true;
  setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2000);
}

/* ============================================================
   9. LAZY IMAGES
   ============================================================ */
function initLazyImages() {
  if ("loading" in HTMLImageElement.prototype) return;
  const imgs = $$('img[loading="lazy"]');
  if (!imgs.length) return;
  const obs = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        const img = e.target;
        if (img.dataset.src) img.src = img.dataset.src;
        img.removeAttribute("loading");
        obs.unobserve(img);
      }
    });
  });
  imgs.forEach((img) => obs.observe(img));
}

/* ============================================================
   10. DATE FORMATTING
   ============================================================ */
function initDateFormatting() {
  $$(".post-date-raw").forEach((el) => {
    const raw = el.dataset.date || el.textContent;
    if (!raw) return;
    try {
      el.textContent = new Date(raw).toLocaleDateString("id-ID", {
        year: "numeric", month: "long", day: "numeric",
      });
      el.classList.remove("post-date-raw");
    } catch { /* biarkan format asli */ }
  });
}

/* ============================================================
   11. RIPPLE EFFECT
   ============================================================ */
function initRippleEffect() {
  $$(".link-btn").forEach((btn) => {
    on(btn, "click", function (e) {
      const ex = this.querySelector(".ripple");
      if (ex) ex.remove();
      const r    = document.createElement("span");
      r.classList.add("ripple");
      const rect = this.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      Object.assign(r.style, {
        width: size + "px", height: size + "px",
        left: e.clientX - rect.left - size / 2 + "px",
        top:  e.clientY - rect.top  - size / 2 + "px",
        position: "absolute", borderRadius: "50%",
        background: "rgba(255,255,255,0.30)",
        transform: "scale(0)", animation: "ripple-anim 0.55s linear",
        pointerEvents: "none",
      });
      this.style.position = "relative";
      this.style.overflow = "hidden";
      this.appendChild(r);
      r.addEventListener("animationend", () => r.remove());
    });
  });

  if (!$("#ripple-style")) {
    const s = document.createElement("style");
    s.id = "ripple-style";
    s.textContent = "@keyframes ripple-anim { to { transform: scale(4); opacity: 0; } }";
    document.head.appendChild(s);
  }
}

/* ============================================================
   12. ANIMATED COUNTERS
   ============================================================ */
function initAnimatedCounters() {
  const pills = $$(".stat-pill__number");
  if (!pills.length || prefersReducedMotion()) return;

  const animate = (el) => {
    const text    = el.textContent.trim();
    const suffix  = text.replace(/[0-9]/g, "");
    const target  = parseInt(text, 10);
    if (isNaN(target)) return;
    const start   = performance.now();
    const tick    = (now) => {
      const t = Math.min((now - start) / 1200, 1);
      el.textContent = Math.round((1 - Math.pow(1 - t, 3)) * target) + suffix;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  if (!("IntersectionObserver" in window)) { pills.forEach(animate); return; }
  const obs = new IntersectionObserver(
    (entries) => entries.forEach((e) => { if (e.isIntersecting) { animate(e.target); obs.unobserve(e.target); } }),
    { threshold: 0.5 },
  );
  pills.forEach((el) => obs.observe(el));
}

/* ============================================================
   13. READING TIME
   ============================================================ */
const READING_WORDS_PER_MINUTE = 200;

function getReadingTimeMinutes(content) {
  const text = (content?.textContent || "").replace(/\s+/g, " ").trim();
  const words = text ? text.split(" ").length : 0;
  return Math.max(1, Math.ceil(words / READING_WORDS_PER_MINUTE));
}

function initReadingTime() {
  $$(".reading-time-badge").forEach((badge) => badge.remove());

  const value = $("#post-read-time-val");
  if (!value) return;

  const minutes = getReadingTimeMinutes($(".post-content"));
  value.textContent = String(minutes);

  const badge = $("#post-read-time");
  if (badge) badge.setAttribute("aria-label", `${minutes} menit baca`);
}

/* ============================================================
   14. CARD 3D TILT — transition dikelola CSS class, bukan
   inline style per-event (hilangkan style recalculation)
   ============================================================ */
function initCardTilt() {
  if (prefersReducedMotion()) return;

  // Inject CSS sekali jika belum ada
  if (!$("#tilt-style")) {
    const s = document.createElement("style");
    s.id = "tilt-style";
    s.textContent = `
      .post-card { transition: transform 0.45s cubic-bezier(0.23,1,0.32,1), box-shadow 0.35s ease; }
      .post-card.is-tilting { transition: transform 0.1s ease; }
    `;
    document.head.appendChild(s);
  }

  $$(".post-card").forEach((card) => {
    on(card, "mousemove", (e) => {
      card.classList.add("is-tilting");
      const r   = card.getBoundingClientRect();
      const cx  = r.width / 2;
      const cy  = r.height / 2;
      const rX  = -((e.clientY - r.top  - cy) / cy) * 5;
      const rY  =  ((e.clientX - r.left - cx) / cx) * 5;
      card.style.transform = `perspective(800px) rotateX(${rX}deg) rotateY(${rY}deg) translateY(-8px)`;
    });
    on(card, "mouseleave", () => {
      card.classList.remove("is-tilting");
      card.style.transform = "";
    });
  });
}

/* ============================================================
   15. TOAST
   ============================================================ */
function showToast(msg, type = "info", duration = 3000) {
  let toast = $("#global-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "global-toast";
    document.body.appendChild(toast);
  }
  const icons = { success: "fa-check-circle", error: "fa-times-circle", info: "fa-info-circle" };
  toast.className = `toast toast--${type}`;
  toast.innerHTML = `<i class="fas ${icons[type] || icons.info}"></i> ${msg}`;
  void toast.offsetWidth; // reflow
  toast.classList.add("is-visible");
  clearTimeout(toast._t);
  toast._t = setTimeout(() => toast.classList.remove("is-visible"), duration);
}

/* ============================================================
   16. KEYBOARD NAV
   ============================================================ */
function initKeyboardNav() {
  const hamburger = $("#hamburger-btn");
  if (hamburger && hamburger.tagName !== "BUTTON") {
    on(hamburger, "keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); hamburger.click(); }
    });
  }
  document.addEventListener("keydown", (e) => {
    if (e.key === "Tab") document.body.classList.add("keyboard-nav");
  });
  document.addEventListener("mousedown", () => document.body.classList.remove("keyboard-nav"));
}

/* ============================================================
   17. FOOTER YEAR
   ============================================================ */
function initFooterYear() {
  const el = $("#footer-year");
  if (el) el.textContent = new Date().getFullYear();
}

/* ============================================================
   18. TYPING EFFECT
   ============================================================ */
function initTypingEffect() {
  const el = $(".profile-tagline[data-typing]");
  if (!el || prefersReducedMotion()) return;
  const texts = (el.dataset.typing || "").split("|").map((t) => t.trim()).filter(Boolean);
  if (texts.length < 2) return;

  let idx = 0, chars = 0, deleting = false;
  el.classList.add("typing-cursor");
  el.textContent = "";

  const tick = () => {
    const cur   = texts[idx];
    el.textContent = cur.slice(0, chars);

    if (!deleting && chars === cur.length) {
      deleting = true;
      return setTimeout(tick, 2000);
    }
    if (deleting && chars === 0) {
      deleting = false;
      idx = (idx + 1) % texts.length;
    }
    chars += deleting ? -1 : 1;
    setTimeout(tick, deleting ? 40 : 80);
  };
  setTimeout(tick, 600);
}

/* ============================================================
   19. SKIP LINK
   ============================================================ */
function initSkipLink() {
  if ($("#skip-link")) return;
  const link = document.createElement("a");
  link.id = "skip-link";
  link.href = "#main-content";
  link.className = "skip-link visually-hidden";
  link.textContent = "Skip to content";
  link.classList.remove("visually-hidden");
  link.classList.add("skip-link");
  document.body.insertAdjacentElement("afterbegin", link);
}

/* ============================================================
   20. HONEYCOMB NETWORK CANVAS
   — Hex-grid distribution, green-gradient nodes, double-circle
     glow, 30fps cap, dist² (no Math.sqrt)
   ============================================================ */
function initNetCanvas() {
  const canvas = $("#net-canvas");
  if (!canvas || prefersReducedMotion()) return;
  /* canvas.js renders its own canvas covering .landing-page at z-index:1,
     making #net-canvas invisible. Skip to avoid a full duplicate animation. */
  if ($('.landing-page')) return;
  const ctx = canvas.getContext("2d");

  /* Node colour palette - navy and gold spectrum */
  const PALETTE = [
    { r: 212, g: 160, b: 23  },
    { r: 240, g: 200, b: 90  },
    { r: 248, g: 228, b: 167 },
    { r: 21,  g: 49,  b: 95  },
    { r: 10,  g: 29,  b: 61  },
  ];

  let nodes = [];

  const buildNodes = () => {
    nodes = [];
    const COLS = 7, ROWS = 5;
    const cw = canvas.width, ch = canvas.height;
    const cellW = cw / COLS, cellH = ch / ROWS;

    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const hexOffset = (row % 2 === 0) ? 0 : cellW * 0.5;
        const c = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        nodes.push({
          x:  (col + 0.5) * cellW + hexOffset + (Math.random() - 0.5) * cellW * 0.55,
          y:  (row + 0.5) * cellH + (Math.random() - 0.5) * cellH * 0.55,
          vx: (Math.random() - 0.5) * 0.28,
          vy: (Math.random() - 0.5) * 0.28,
          radius: Math.random() * 2 + 1.8,
          r: c.r, g: c.g, b: c.b,
        });
      }
    }
  };

  const resize = () => {
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    buildNodes();
  };
  resize();
  window.addEventListener("resize", resize, { passive: true });

  const MAX_DIST    = 190;            // jarak max koneksi lebih jauh
  const MAX_DIST_SQ = MAX_DIST * MAX_DIST;
  const INTERVAL    = 1000 / 30;

  let lastTime = 0, active = true;
  const TWO_PI = Math.PI * 2;

  const draw = (now = 0) => {
    requestAnimationFrame(draw);
    if (!active || now - lastTime < INTERVAL) return;
    lastTime = now;

    const cw = canvas.width, ch = canvas.height;
    ctx.clearRect(0, 0, cw, ch);

    /* — Move nodes — */
    for (const n of nodes) {
      n.x += n.vx; n.y += n.vy;
      if (n.x < 0 || n.x > cw) n.vx *= -1;
      if (n.y < 0 || n.y > ch) n.vy *= -1;
    }

    /* — Collect visible edges (single O(n²) pass) — */
    const edges = [];
    for (let i = 0; i < nodes.length; i++) {
      const ni = nodes[i];
      for (let j = i + 1; j < nodes.length; j++) {
        const nj = nodes[j];
        const dx = ni.x - nj.x, dy = ni.y - nj.y;
        const dSq = dx * dx + dy * dy;
        if (dSq >= MAX_DIST_SQ) continue;
        edges.push({ xi: ni.x, yi: ni.y, xj: nj.x, yj: nj.y, t: 1 - dSq / MAX_DIST_SQ });
      }
    }

    /* Canvas-wide diagonal gradient — applied to all edges */
    const grad = ctx.createLinearGradient(0, ch, cw, 0); // bottom-left → top-right
    grad.addColorStop(0,    '#00c850');
    grad.addColorStop(0.35, '#64f000');
    grad.addColorStop(0.7,  '#00e878');
    grad.addColorStop(1,    '#b4ff00');

    /* — Edge pass 1: glow shadow (batched, uniform low alpha) — */
    ctx.beginPath();
    ctx.strokeStyle = grad;
    ctx.lineWidth   = 6;
    ctx.globalAlpha = 0.09;
    for (const e of edges) { ctx.moveTo(e.xi, e.yi); ctx.lineTo(e.xj, e.yj); }
    ctx.stroke();

    /* — Edge pass 2: main lines (per-edge alpha, gradient colour) — */
    ctx.strokeStyle = grad;
    for (const e of edges) {
      ctx.globalAlpha = e.t * 0.92;
      ctx.lineWidth   = e.t > 0.6 ? 2.0 : 1.2;
      ctx.beginPath();
      ctx.moveTo(e.xi, e.yi);
      ctx.lineTo(e.xj, e.yj);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    /* — Draw nodes: layered glow + bright core + white spark — */
    for (const n of nodes) {
      /* Outer diffuse glow */
      ctx.fillStyle = `rgba(${n.r},${n.g},${n.b},0.18)`;
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.radius * 4.2, 0, TWO_PI);
      ctx.fill();

      /* Mid halo */
      ctx.fillStyle = `rgba(${n.r},${n.g},${n.b},0.44)`;
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.radius * 2.1, 0, TWO_PI);
      ctx.fill();

      /* Coloured core */
      ctx.fillStyle = `rgba(${n.r},${n.g},${n.b},0.92)`;
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.radius, 0, TWO_PI);
      ctx.fill();

      /* White spark centre */
      ctx.fillStyle = 'rgba(255,255,255,0.82)';
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.radius * 0.42, 0, TWO_PI);
      ctx.fill();
    }
  };

  requestAnimationFrame(draw);

  if ("IntersectionObserver" in window) {
    new IntersectionObserver(
      ([e]) => { active = e.isIntersecting; },
      { threshold: 0 },
    ).observe(canvas);
  }
}

/* ============================================================
   21. CURSOR GLOW (landing page only)
   — Update posisi langsung di mousemove (no continuous RAF)
   ============================================================ */
function initCursorGlow() {
  if (prefersReducedMotion()) return;
  const landing = $(".landing-page");
  if (!landing) return;

  const glow = document.createElement("div");
  glow.className = "cursor-glow";
  landing.appendChild(glow);

  // Update posisi langsung via CSS custom properties (no RAF loop)
  landing.addEventListener("mousemove", (e) => {
    const r = landing.getBoundingClientRect();
    glow.style.left    = e.clientX - r.left + "px";
    glow.style.top     = e.clientY - r.top  + "px";
    glow.style.opacity = "1";
  }, { passive: true });

  landing.addEventListener("mouseleave", () => {
    glow.style.opacity = "0";
  });
}

/* ============================================================
   22. COMMAND PALETTE (Ctrl+K / Cmd+K)
   ============================================================ */
function initCommandPalette() {
  // Collect navigation items
  const navItems = [
    ...Array.from($$(".site-nav a, .mobile-nav a")).map((a) => ({
      label: a.textContent.trim(),
      href:  a.getAttribute("href") || "#",
      icon:  "fa-link",
      group: "Navigasi",
    })),
    ...Array.from($$(".link-btn")).map((a) => ({
      label: a.textContent.trim(),
      href:  a.getAttribute("href") || "#",
      icon:  a.querySelector("i")?.className?.split(" ").pop() || "fa-external-link",
      group: "Tautan",
    })),
  ].filter((item, i, arr) => arr.findIndex((x) => x.href === item.href) === i);

  // Build DOM
  const overlay = document.createElement("div");
  overlay.className = "cmd-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="cmd-palette" role="combobox">
      <div class="cmd-palette__header">
        <i class="fas fa-search"></i>
        <input class="cmd-palette__input" type="text" placeholder="Cari halaman atau tautan…" autocomplete="off"/>
        <kbd class="cmd-palette__kbd">ESC</kbd>
      </div>
      <div class="cmd-palette__results" id="cmd-results"></div>
      <div class="cmd-palette__footer">
        <span class="cmd-palette__footer-hint"><kbd class="cmd-palette__kbd">↑↓</kbd> navigasi</span>
        <span class="cmd-palette__footer-hint"><kbd class="cmd-palette__kbd">↵</kbd> buka</span>
        <span class="cmd-palette__footer-hint"><kbd class="cmd-palette__kbd">ESC</kbd> tutup</span>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const input    = overlay.querySelector(".cmd-palette__input");
  const results  = overlay.querySelector("#cmd-results");
  let activeIdx  = -1;
  let searchSeq  = 0; // token untuk menghindari race condition antar-request

  const renderItems = (items, { loading = false } = {}) => {
    if (loading) {
      results.innerHTML = `<p style="padding:1rem 1.5rem;color:var(--text-light);font-size:.875rem;font-family:var(--font-heading);">Mencari…</p>`;
      activeIdx = -1;
      return;
    }
    if (!items.length) {
      results.innerHTML = `<p style="padding:1rem 1.5rem;color:var(--text-light);font-size:.875rem;font-family:var(--font-heading);">Tidak ada hasil</p>`;
      activeIdx = -1;
      return;
    }
    const groupOrder = ["Artikel", "Navigasi", "Tautan"];
    const groups = {};
    items.forEach((item) => {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    });
    let flatIdx = 0;
    results.innerHTML = Object.keys(groups)
      .sort((a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b))
      .map((g) => `
        <div class="cmd-palette__group-label">${g}</div>
        ${groups[g].map((item) => `
          <a class="cmd-palette__item" href="${item.href}" data-idx="${flatIdx++}">
            <i class="fas ${item.icon}"></i>
            <span class="cmd-palette__item-text">${item.label}</span>
            <span class="cmd-palette__item-hint">${item.group}</span>
          </a>`).join("")}
      `).join("");
    activeIdx = -1;
  };

  // Cari artikel sungguhan lewat JSON feed bawaan Blogger (same-origin, tanpa CORS).
  const searchPosts = async (q) => {
    try {
      const res = await fetch(
        `/feeds/posts/default?q=${encodeURIComponent(q)}&max-results=6&alt=json`
      );
      if (!res.ok) return [];
      const data = await res.json();
      const entries = data?.feed?.entry || [];
      return entries.map((entry) => ({
        label: entry.title?.$t || "(Tanpa judul)",
        href:  entry.link?.find((l) => l.rel === "alternate")?.href || "#",
        icon:  "fa-newspaper",
        group: "Artikel",
      }));
    } catch {
      return []; // gagal fetch (offline dll) — jangan sampai mematikan palette
    }
  };

  const open = () => {
    overlay.classList.add("is-open");
    renderItems(navItems);
    setTimeout(() => input.focus(), 50);
    document.body.style.overflow = "hidden";
  };

  const close = () => {
    overlay.classList.remove("is-open");
    input.value = "";
    document.body.style.overflow = "";
  };

  const setActive = (idx) => {
    const items = results.querySelectorAll(".cmd-palette__item");
    items.forEach((el, i) => el.classList.toggle("is-active", i === idx));
    if (items[idx]) items[idx].scrollIntoView({ block: "nearest" });
    activeIdx = idx;
  };

  let debounceTimer = null;
  on(input, "input", () => {
    const raw = input.value.trim();
    const q   = raw.toLowerCase();
    const navMatches = q ? navItems.filter((x) => x.label.toLowerCase().includes(q)) : navItems;

    clearTimeout(debounceTimer);

    if (!raw) {
      renderItems(navMatches);
      return;
    }

    // Tampilkan hasil navigasi dulu supaya palette tidak terasa kosong,
    // lalu susulkan hasil artikel begitu fetch selesai.
    renderItems(navMatches);

    if (raw.length < 2) return;

    const seq = ++searchSeq;
    debounceTimer = setTimeout(async () => {
      renderItems(navMatches, { loading: true });
      const postMatches = await searchPosts(raw);
      if (seq !== searchSeq) return; // input sudah berubah, buang hasil basi
      renderItems([...postMatches, ...navMatches]);
    }, 300);
  });

  overlay.addEventListener("keydown", (e) => {
    const items = results.querySelectorAll(".cmd-palette__item");
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(Math.min(activeIdx + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(Math.max(activeIdx - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      items[activeIdx]?.click();
      close();
    } else if (e.key === "Escape") {
      close();
    }
  });

  on(overlay, "click", (e) => { if (e.target === overlay) close(); });

  document.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
      e.preventDefault();
      overlay.classList.contains("is-open") ? close() : open();
    }
  });

  // Command palette trigger button (jika ada di template)
  $$("[data-cmd-palette]").forEach((btn) => on(btn, "click", open));
}

/* ============================================================
   23. TABLE OF CONTENTS
   ============================================================ */
function initTOC() {
  const content = $(".post-content");
  if (!content) return;

  const headings = $$("h2, h3", content);

  // Beri id pada heading yang belum punya
  headings.forEach((h, i) => { if (!h.id) h.id = `heading-${i}`; });

  const sidebarNav = document.getElementById("sidebar-toc-nav");
  const tocWidget  = document.getElementById("sidebar-toc");

  if (sidebarNav && headings.length >= 2) {
    // ─── Mode sidebar: isi #sidebar-toc-nav dan tampilkan widget ───
    const ol = document.createElement("ol");
    ol.className = "toc-list";
    headings.forEach((h) => {
      const li = document.createElement("li");
      li.className = `toc-item${h.tagName === "H3" ? " toc-item--h3" : ""}`;
      const a = document.createElement("a");
      a.href = "#" + h.id;
      a.className = "toc-link";
      a.textContent = h.textContent.trim();
      li.appendChild(a);
      ol.appendChild(li);
    });
    sidebarNav.appendChild(ol);
    if (tocWidget && ol.children.length > 0) tocWidget.style.display = "block";
  } else if (!sidebarNav && headings.length >= 3) {
    // ─── Fallback inline (tidak ada sidebar, mis. tablet/mobile) ───
    const toc = document.createElement("div");
    toc.className = "post-toc slide-up";
    toc.innerHTML = `
      <div class="post-toc__title"><i class="fas fa-list-ul"></i> Daftar Isi</div>
      <ol class="post-toc__list">
        ${Array.from(headings).map((h) => `
          <li class="post-toc__item ${h.tagName === "H3" ? "post-toc__item--h3" : ""}">
            <a href="#${h.id}" class="post-toc__link">${h.textContent.trim()}</a>
          </li>`).join("")}
      </ol>`;
    content.insertAdjacentElement("beforebegin", toc);
  } else {
    return;
  }

  // ─── IntersectionObserver: highlight heading aktif ─────────────
  if (!("IntersectionObserver" in window)) return;
  const links = $$(".toc-link, .post-toc__link");
  if (!links.length) return;

  const obs = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          links.forEach((l) => l.classList.remove("is-active"));
          const active = document.querySelector(
            `.toc-link[href="#${e.target.id}"], .post-toc__link[href="#${e.target.id}"]`
          );
          if (active) active.classList.add("is-active");
        }
      });
    },
    { rootMargin: "-20% 0px -75% 0px" },
  );
  headings.forEach((h) => obs.observe(h));
}

/* ============================================================
   24. CODE COPY BUTTON
   ============================================================ */
function initCodeCopy() {
  const content = $(".post-content");
  if (!content) return;

  $$("pre", content).forEach((pre) => {
    if (pre.querySelector(".code-copy-btn")) return;
    const btn = document.createElement("button");
    btn.className = "code-copy-btn";
    btn.innerHTML = `<i class="fas fa-copy"></i> Copy`;
    btn.setAttribute("aria-label", "Copy code");

    on(btn, "click", async (e) => {
      e.stopPropagation();
      const code = pre.querySelector("code")?.textContent ?? pre.textContent;
      try {
        await navigator.clipboard.writeText(code);
      } catch {
        const inp = document.createElement("textarea");
        inp.value = code;
        inp.style.position = "fixed";
        inp.style.opacity = "0";
        document.body.appendChild(inp);
        inp.select();
        document.execCommand("copy");
        document.body.removeChild(inp);
      }
      btn.innerHTML = `<i class="fas fa-check"></i> Disalin`;
      btn.classList.add("copied");
      setTimeout(() => {
        btn.innerHTML = `<i class="fas fa-copy"></i> Copy`;
        btn.classList.remove("copied");
      }, 2000);
    });

    pre.style.position = "relative";
    pre.appendChild(btn);
  });
}

/* ============================================================
   25. IMAGE LIGHTBOX
   ============================================================ */
function initLightbox() {
  const content = $(".post-content");
  if (!content) return;

  // Build overlay
  const overlay = document.createElement("div");
  overlay.className = "lightbox-overlay";
  overlay.innerHTML = `
    <button class="lightbox-close" aria-label="Tutup"><i class="fas fa-times"></i></button>
    <img class="lightbox-img" src="" alt=""/>`;
  document.body.appendChild(overlay);

  const img   = overlay.querySelector(".lightbox-img");
  const close = overlay.querySelector(".lightbox-close");

  const openLightbox = (src, alt) => {
    img.src = src;
    img.alt = alt || "";
    overlay.classList.add("is-open");
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    overlay.classList.remove("is-open");
    document.body.style.overflow = "";
  };

  on(close, "click", closeLightbox);
  on(overlay, "click", (e) => { if (e.target === overlay) closeLightbox(); });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeLightbox(); });
  on(img, "click", (e) => e.stopPropagation()); // prevent bubble to overlay

  $$("img", content).forEach((el) => {
    on(el, "click", () => openLightbox(el.src, el.alt));
  });
}

/* ============================================================
   26. LIVE CLOCK (landing page)
   ============================================================ */
function initLiveClock() {
  const clockEl = $("#landing-clock-time");
  if (!clockEl) return;

  const update = () => {
    const now = new Date();
    const hh  = String(now.getHours()).padStart(2, "0");
    const mm  = String(now.getMinutes()).padStart(2, "0");
    const ss  = String(now.getSeconds()).padStart(2, "0");
    clockEl.textContent = `${hh}:${mm}:${ss} WIB`;
  };
  update();
  setInterval(update, 1000);
}

/* ============================================================
   27. ADSENSE SLOTS
   ============================================================ */
function initAdSenseSlots() {
  const ads = $$(".adsbygoogle");
  if (!ads.length) return;

  ads.forEach((ad) => {
    const client = ad.getAttribute("data-ad-client") || "";
    const slot   = ad.getAttribute("data-ad-slot") || "";
    const holder = ad.closest(".ad-slot");
    const isReady = client.startsWith("ca-pub-") && !client.includes("[") && slot && !slot.includes("[");

    if (!isReady) return;

    holder?.classList.add("is-configured");
    if (ad.dataset.adsenseLoaded === "true") return;

    // Sembunyikan slot kalau AdSense tidak berhasil mengisi iklan (unfilled,
    // belum disetujui, atau diblokir ad-blocker) — supaya tidak ada kotak
    // kosong yang memakan ruang halaman.
    if (holder) {
      let settled = false;
      const hideIfUnfilled = () => {
        if (settled) return;
        settled = true;
        if (ad.getAttribute("data-ad-status") !== "filled") {
          holder.classList.add("is-unfilled");
        }
      };
      new MutationObserver((_, obs) => {
        if (ad.hasAttribute("data-ad-status")) {
          hideIfUnfilled();
          obs.disconnect();
        }
      }).observe(ad, { attributes: true, attributeFilter: ["data-ad-status"] });
      setTimeout(hideIfUnfilled, 4000); // fallback: script gagal dimuat / diblokir
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      ad.dataset.adsenseLoaded = "true";
    } catch {
      holder?.classList.remove("is-configured");
      holder?.classList.add("is-unfilled");
    }
  });
}

/* ============================================================
   INISIALISASI UTAMA
   ============================================================ */
function init() {
  initSkipLink();
  initHamburger();
  initUnifiedScroll();   // ← menggantikan 4 scroll handler terpisah
  initSmoothScroll();
  initScrollAnimations();
  initExternalLinks();
  initActiveLinks();
  initDarkMode();
  initAdSenseSlots();
  initArticleShare();
  initCopyLink();
  initLazyImages();
  initDateFormatting();
  initRippleEffect();
  initAnimatedCounters();
  initReadingTime();
  initCardTilt();
  initKeyboardNav();
  initFooterYear();
  initTypingEffect();

  // High-tech features
  initNetCanvas();
  initCursorGlow();
  initCommandPalette();
  initTOC();
  initCodeCopy();
  initLightbox();
  initLiveClock();

  document.body.classList.remove("loading");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
